import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Award, Copy, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

const STEPS = [
  "On the 1st of each month, pick the month that just ended (selected by default).",
  "Check the #1 family below. Only families who opted in to the board can win.",
  "Click Award certificate. The family gets an email and WhatsApp link to print or share it.",
  "One certificate per month. If no opted-in family brought friends, skip that month.",
];

export const AmbassadorAward = ({ auth, onErr }) => {
  const [month, setMonth] = useState("");
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/ambassadors", { ...auth, params: month ? { month } : {} })
    .then((r) => { setD(r.data); if (!month) setMonth(r.data.month); }).catch(onErr), [auth, month, onErr]);
  useEffect(() => { load(); }, [load]);
  const award = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/ambassadors/award", { month }, auth); toast.success(`Certificate awarded to ${data.name}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not award"); }
    finally { setBusy(false); }
  };
  const copy = (t) => { navigator.clipboard?.writeText(`${window.location.origin}/ambassador/${t}`); toast.success("Certificate link copied"); };
  if (!d) return <Loader2 className="w-5 h-5 animate-spin" />;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 grid lg:grid-cols-[1.1fr_1fr] gap-6" data-testid="ambassador-award">
      <div>
        <h3 className="font-bold text-[#0A192F] flex items-center gap-2"><Award className="w-4 h-4 text-[#FF2E63]" />Ambassador of the Month</h3>
        <ol className="mt-3 space-y-1.5 text-sm text-slate-600 list-decimal pl-5" data-testid="ambassador-guide">{STEPS.map((s) => <li key={s}>{s}</li>)}</ol>
      </div>
      <div className="space-y-3">
        <div className="flex gap-2 items-center">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44 h-9" data-testid="ambassador-month" />
          {d.awarded ? <span className="text-sm font-bold text-emerald-700" data-testid="ambassador-awarded">Awarded to {d.awarded.name}</span>
            : <Button onClick={award} disabled={busy || !d.candidate} className="h-9 rs-cta rounded-full" data-testid="ambassador-award-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Award certificate"}</Button>}
        </div>
        <p className="text-sm" data-testid="ambassador-candidate">{d.candidate ? <>Top family: <b>{d.candidate.name}</b> · {d.candidate.city} · {d.candidate.friends} friends</> : <span className="text-slate-500">No opted-in family brought friends this month.</span>}</p>
        <div className="space-y-1 max-h-32 overflow-y-auto">{d.awards.map((a) => (
          <div key={a.id} className="flex items-center justify-between text-xs border-t pt-1" data-testid={`ambassador-past-${a.month}`}>
            <span><b>{a.month}</b> · {a.name} · {a.friends} friends</span>
            <button onClick={() => copy(a.token)} className="inline-flex items-center gap-1 text-[#007A8A]" data-testid={`ambassador-copy-${a.month}`}><Copy className="w-3 h-3" />link</button>
          </div>))}</div>
      </div>
    </div>
  );
};
